// Stream 6 — Hermes operator authority status classifier (honest correction).
//
// 2026-06-14 DÜZELTME: İlk scaffold, Hermes'i bir principal_permission_grants
// principal'ı sayıp DB-grant ("active") modeli kurmuştu. DB doğrulaması (Yılmaz
// 2026-06-14 SSH) bunun YANLIŞ olduğunu gösterdi:
//   - `agents` tablosunda Hermes YOK (dış Telegram bot + bridge castintech-hermes:8092)
//   - principal_permission_grants sadece 'agent' (paperclip) + 'user' (board) principal'ları tutar
//   - Hermes ikisinden de değil → grant'lanabilir bir principal_id YOK
//
// Doğru model: Hermes propose-only ([[concepts/ecosystem-process-map]] § G —
// "ASLA tek başına execute etmez"). Council onayı = governance yetkisi; teknik
// enforcement (varsa) Hermes bridge tarafında, DB-grant DEĞİL. Bu yüzden bir
// council kararı 'approved' olduğunda Hermes governance-olarak yetkilidir —
// "pending technical activation" yanlış framing'di.
//
// Status modeli (DB-capability kavramı kaldırıldı):
//   not_submitted   — hiç matching council karar yok
//   submitted_open  — karar açık, council/insan kararı bekliyor
//   council_approved — council onayladı = governance yetkisi aktif (propose-only)
//   rejected        — son karar reddedildi/expired
//   unknown_error   — beklenmedik status

import type { CouncilDecision } from '../views/agent-council.js';

export type HermesCategory = 'dashboard' | 'meetings' | 'agent-message';

export type HermesAuthorityStatus =
  | 'not_submitted'
  | 'submitted_open'
  | 'council_approved'
  | 'rejected'
  | 'unknown_error';

export interface HermesCategoryStatus {
  category: HermesCategory;
  status: HermesAuthorityStatus;
  latestDecisionId?: string;
}

// Kategori keyword'leri — title + reason + subjectId + decisionType birleşik metin
// üzerinde regex ile tespit. False positive minimum için spesifik.
const CATEGORY_PATTERNS: Record<HermesCategory, RegExp> = {
  dashboard:
    /(hermes.*pano|hermes.*dashboard|operator.*authority|dashboard-operator-authority|diagnose.and.remediate)/,
  meetings:
    /(meeting|toplant[ıi]).*(participation|join|comment|kat[ıi])|(hermes.*meeting|hermes.*toplant)/,
  'agent-message':
    /(agent.message|inter.agent|cross.agent|agent.communication|nexus.*kon[uü][şs]|hermes.*nexus)/,
};

export function detectHermesCategory(d: CouncilDecision): HermesCategory | null {
  const ctx = d.context as Record<string, unknown> | null;
  const title = (ctx && typeof ctx.title === 'string' ? ctx.title : '') as string;
  const haystack = [title, d.reason || '', d.subjectId || '', d.decisionType || '']
    .join(' ')
    .toLowerCase();
  // İlk eşleşen kategori — sıralama: dashboard > meetings > agent-message
  // (dashboard ifadesi daha sık operator-authority ile bağlı, öncelik)
  for (const cat of ['dashboard', 'meetings', 'agent-message'] as HermesCategory[]) {
    if (CATEGORY_PATTERNS[cat].test(haystack)) return cat;
  }
  return null;
}

export function isHermesAuthorityDecision(d: CouncilDecision): boolean {
  return detectHermesCategory(d) !== null;
}

/**
 * Her kategori için latest karar bazlı status sınıflandır.
 *
 * Hermes propose-only — DB-grant capability kavramı YOK. Bir kategori için en
 * yeni karar 'approved' ise governance-olarak yetkilidir → 'council_approved'.
 * Sahte "active" (DB-grant) ya da yanıltıcı "pending activation" yok.
 */
export function classifyHermesAuthorityStatus(
  decisions: CouncilDecision[],
): HermesCategoryStatus[] {
  const categories: HermesCategory[] = ['dashboard', 'meetings', 'agent-message'];
  return categories.map((cat) => {
    const matched = decisions
      .filter((d) => detectHermesCategory(d) === cat)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    if (matched.length === 0) {
      return { category: cat, status: 'not_submitted' as HermesAuthorityStatus };
    }
    const latest = matched[0];
    let status: HermesAuthorityStatus;
    if (latest.status === 'open') {
      status = 'submitted_open';
    } else if (latest.status === 'rejected' || latest.status === 'expired') {
      status = 'rejected';
    } else if (latest.status === 'approved') {
      status = 'council_approved';
    } else {
      status = 'unknown_error';
    }
    return { category: cat, status, latestDecisionId: latest.id };
  });
}

/**
 * Aggregate — 3 kategori statüslerini tek özet status'a indirger.
 *
 * Öncelik (en kötü/eksik kazanır, ama council_approved olumlu terminal):
 *   rejected > submitted_open > council_approved > not_submitted
 *
 * Hepsi council_approved → council_approved (governance yetkisi tam).
 * Herhangi rejected → rejected. Bekleyen açık karar varsa → submitted_open.
 */
export function aggregateStatus(
  perCategory: HermesCategoryStatus[],
): HermesAuthorityStatus {
  const statuses = perCategory.map((x) => x.status);
  if (statuses.length === 0) return 'not_submitted';
  if (statuses.some((s) => s === 'rejected')) return 'rejected';
  if (statuses.some((s) => s === 'submitted_open')) return 'submitted_open';
  if (statuses.some((s) => s === 'council_approved')) return 'council_approved';
  if (statuses.every((s) => s === 'not_submitted')) return 'not_submitted';
  return 'unknown_error';
}

// ── UI helpers (view'lar tüketir) ────────────────────────────────────────

export const CATEGORY_LABEL: Record<HermesCategory, string> = {
  dashboard: 'Pano Operatörü',
  meetings: 'Toplantı Katılımı',
  'agent-message': 'Ajan Mesajlaşma',
};

export const STATUS_LABEL: Record<HermesAuthorityStatus, string> = {
  not_submitted: '— Hiç istek yok',
  submitted_open: '🟠 Council kararı bekliyor',
  council_approved: '🟢 Council-Onaylı (propose-only)',
  rejected: '🔴 Reddedildi',
  unknown_error: '⚠️ Bilinmeyen',
};

export const STATUS_TONE: Record<HermesAuthorityStatus, string> = {
  not_submitted: 'gray',
  submitted_open: 'orange',
  council_approved: 'emerald',
  rejected: 'red',
  unknown_error: 'gray',
};
